import { useState, useCallback, useEffect, useRef } from "react";
import { XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { logger } from "./helpers/logHandler";
import { playError, playSuccess } from "./helpers/audioPlayer";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useTranslation } from "../i18n";
import FitWord from "./FitWord";
import { apiFetch } from "../api/apiFetch";
import { zoneDispatchReasonKey } from "./sorterTwo/zoneDispatchReasons";

type ScannedItem = { OrderNumber: string; ItemSku: string };

// One refused order from the BE's singles close (reason "orders_refused"):
// which items it covers and why it failed.
type SinglesFailure = {
    order_number?: string | null;
    item_skus?: string[];
    reason?: string;
    status?: string;
};

const ZoneStorageSorter1UnloadSingles = ({ warehouse, workingLocation, set_workingLocation, closeUnload, flashScanStatus, scanValue, onScanChange, registerSubmit }: { warehouse: string, workingLocation: string, set_workingLocation: React.Dispatch<React.SetStateAction<string>>, closeUnload: () => void, flashScanStatus: (status: "success" | "error") => void, scanValue: string, onScanChange: React.Dispatch<React.SetStateAction<string>>, registerSubmit: (fn: (scannedValue?: string) => void) => void }) => {

    const navigator = useNavigate();
    const { t } = useTranslation();

    const [workingTag, set_workingTag] = useState<string>("");
    const [savedTag, set_savedTag] = useState<string>("");
    const [scannedItems, set_scannedItems] = useState<ScannedItem[]>([]);
    // Items Portal refused on the last close attempt, keyed by SKU, with
    // the i18n key of the reason. Cleared as the operator removes them.
    const [failedItems, set_failedItems] = useState<Record<string, string>>({});
    // One tote pre-check / one close in flight at a time.
    const busyRef = useRef(false);

    const locationBarcodePrefix = warehouse === "Newport" ? "NPS" : "PLS";
    const laneBarcode = warehouse === "Newport" ? "NPL02" : "PLL02";

    const handleTagInput = useCallback(async (scannedValue?: string) => {
        const val = scannedValue ?? scanValue;
        if (val.slice(0, 3) === "NPS" || val.slice(0, 3) === "PLS" || val.slice(0, 3) === "LPN" || val.slice(0, 2).toLowerCase() === "sp" || val.length > 8 || val === "") {
            onScanChange("");
            playError();
            logger(warehouse, "S1 Unloader", "Singles", 0, val, "Open Tag", "N/A", "N/A", "error");
            return;
        }
        onScanChange("");
        if (busyRef.current) return;
        busyRef.current = true;
        // Pre-check with the BE: is this tote free? No order is known yet,
        // so only the tote is judged (zone_singles_dispatch_via_portal). With
        // the switch off the BE answers ok, and a network failure is not a
        // refusal — the lane scan re-checks everything under locks.
        let refusal: { reason?: string; status?: string } | null = null;
        try {
            const res = await apiFetch(`/zonestorage/orders/check-tag`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse, tag: val }),
            });
            if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
            if (res.ok) {
                const data = await res.json();
                if (data && data.ok === false) refusal = data;
            }
        } catch {
            refusal = null;
        } finally {
            busyRef.current = false;
        }
        if (refusal) {
            flashScanStatus("error");
            playError();
            toast.dismiss();
            toast.error(t(zoneDispatchReasonKey(refusal.reason), { status: refusal.status ?? "", order: "" }));
            logger(warehouse, "S1 Unloader", "Singles", 0, val, "Open Tag", "N/A", refusal.reason ?? "refused", "error");
            return;
        }
        set_workingTag(val);
        set_failedItems({});
        logger(warehouse, "S1 Unloader", "Singles", 0, val, "Open Tag", "N/A", "N/A", "success");
    }, [scanValue, onScanChange, warehouse, navigator, flashScanStatus, t]);

    const handleLaneInput = useCallback(async (scannedValue?: string) => {
        const scan = scannedValue ?? scanValue;
        onScanChange("");

        // Check if scan is the required lane
        if (scan !== laneBarcode) {
            flashScanStatus("error");
            playError();
            logger(warehouse, "S1 Unloader", "Many", 0, scan, "Lane Location", savedTag, scan, "error");
            return;
        }
        if (busyRef.current) return;
        busyRef.current = true;
        try {
            // Save order in back end. With Portal dispatch on, this is the
            // close that binds every order on the tag to the tote; a refusal
            // names the items to pull and the tag reopens for editing.
            const res = await apiFetch(`/zonestorage/singleitemorder/new`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse: warehouse, tag: savedTag, itemsList: scannedItems }),
            });
            if (res.status === 401) { logoutProcessor(); navigator("/login"); return; }
            if (res.ok) {
                logger(warehouse, "S1 Unloader", "Many", 0, scan, "Lane Location", savedTag, scan, "success");
                closeUnload();
                return;
            }

            let response: any = null;
            try { response = await res.json(); } catch { response = null; }
            console.error("Error creating order", response);
            logger(warehouse, "S1 Unloader", "Many", 0, scan, "Lane Location", savedTag, response?.reason ?? scan, "error");
            flashScanStatus("error");
            playError();
            toast.dismiss();

            if (response && typeof response === "object" && response.reason === "orders_refused") {
                const failures: SinglesFailure[] = Array.isArray(response.failures) ? response.failures : [];
                const marks: Record<string, string> = {};
                for (const f of failures) {
                    for (const sku of f.item_skus ?? []) marks[sku] = zoneDispatchReasonKey(f.reason);
                }
                set_failedItems(marks);
                toast.error(t("zone_singles_refused", { count: Object.keys(marks).length || failures.length }));
                // Reopen the tag so the list shows with the failures marked;
                // the operator removes them, closes the tag and scans the
                // lane again.
                set_workingTag(savedTag);
                set_savedTag("");
            } else if (response && typeof response === "object" && response.reason) {
                toast.error(t(zoneDispatchReasonKey(response.reason), { status: response.status ?? "", order: "" }));
                // A tote-level refusal: the tag itself is the problem, so
                // reopen it for a different tote rather than leaving the
                // operator stuck on the lane prompt.
                set_workingTag(savedTag);
                set_savedTag("");
            } else {
                toast.error(typeof response === "string" ? response : t("scan_failed"));
            }
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss();
            toast.error(t("scan_failed"));
        } finally {
            busyRef.current = false;
        }
    }, [scanValue, onScanChange, laneBarcode, savedTag, scannedItems, warehouse, closeUnload, flashScanStatus, navigator, t]);

    const handleOtherInput = useCallback(async (scannedValue?: string) => {
        const scan = scannedValue ?? scanValue;
        onScanChange("");

        // Check if scan is a location
        if (locationBarcodePrefix === scan.substring(0, 3)) {
            // Check if the location is the same as the working location
            if (scan.slice(-2) !== workingLocation) {
                // Wrong location
                flashScanStatus("error");
                playError();
            } else {
                // Same as working location
                flashScanStatus("success");
                playSuccess();
                set_workingLocation(scan);
            }

        } else if (scan === workingTag) {
            // Scanned the tag and so close tag and send to backend
            set_savedTag(workingTag);
            set_workingTag("");
            logger(warehouse, "S1 Unloader", "Singles", 0, scan, "Close Tag", "N/A", "N/A", "success");

        } else {

            if (!scannedItems.every((item) => item.ItemSku !== scan)) {
                flashScanStatus("success");
                playSuccess();
                logger(warehouse, "S1 Unloader", scan, 0, scan, "Item", workingLocation, workingTag, "repeat");
                return;
            }

            await apiFetch(`/zonestorage/iteminfo/check-single-product-order`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ warehouse: warehouse, itemSku: scan })
            }).then(async (res): Promise<any> => {

                if (res.status === 401) {
                    console.log("INVALID PERMISSIONS");
                    logoutProcessor();
                    navigator("/login");
                    return;
                }

                const response = await res.json();

                if (res.ok) {
                    if (response.message === "Right Location") {

                        logger(warehouse, "S1 Unloader", scan, response.item.weight, scan, "Item", workingLocation, workingTag, "success");

                        delete response.item.weight;
                        set_scannedItems([response.item, ...scannedItems]);

                        flashScanStatus("success");
                        playSuccess();
                    } else {
                        flashScanStatus("error");
                        playError();
                        logger(warehouse, "S1 Unloader", scan, 0, scan, "Item", workingLocation, workingTag, "error");
                    }
                } else {
                    console.log("ERROR:", response.error);
                    logger(warehouse, "S1 Unloader", scan, 0, scan, "Item", workingLocation, workingTag, "error");
                }
            });
        }
    }, [scanValue, onScanChange, locationBarcodePrefix, workingLocation, workingTag, scannedItems, warehouse, navigator, flashScanStatus, set_workingLocation]);

    const handleSubmit = useCallback((scannedValue?: string) => {
        if (workingTag === "" && savedTag === "") {
            handleTagInput(scannedValue);
        } else if (savedTag !== "") {
            handleLaneInput(scannedValue);
        } else {
            handleOtherInput(scannedValue);
        }
    }, [workingTag, savedTag, handleTagInput, handleLaneInput, handleOtherInput]);

    useEffect(() => {
        registerSubmit(handleSubmit);
    }, [handleSubmit, registerSubmit]);

    const removeSpecific = (sku: string) => {
        set_scannedItems(scannedItems.filter((item) => item.ItemSku !== sku));
        if (failedItems[sku]) {
            const next = { ...failedItems };
            delete next[sku];
            set_failedItems(next);
        }
    }

    return (
        <div>
            <h2 className="align-center sorter-1-unloader-location-title">{t("location_to_lane", { location: workingLocation })}</h2>
            {(workingTag === "" && savedTag === "") &&
                <div className="flex flex-column">
                    <div className="scan-command">
                        {t("scan_tag").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                    <div className="scan-feedback-area">
                        <p>{t("single_item_orders")}</p>
                    </div>
                </div>}

            {workingTag !== "" &&
                <div className="flex flex-column full-width">
                    <h1 className="tiny-gap">{t("single_item_orders")}</h1>
                    <div className="scan-feedback-area">
                        <h2 className="tiny-gap-around">{t("tag_label", { tag: workingTag })}</h2>
                    </div>
                    <div className="zone-storage-lite-container">
                        <h2 className="no-margin tiny-gap">{t("scanned_items")}</h2>
                        <div className="zone-storage-lite-item-list">
                            {scannedItems.map((item) => {
                                const failedKey = failedItems[item.ItemSku];
                                return (
                                    <div
                                        key={item.ItemSku}
                                        className={`zone-storage-lite-item${failedKey ? " zone-storage-lite-item--failed" : ""}`}
                                        title={failedKey ? t(failedKey, { order: item.OrderNumber, status: "" }) : undefined}
                                    >
                                        <p>{item.ItemSku}</p>
                                        <XCircle onClick={() => removeSpecific(item.ItemSku)} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>}

            {(savedTag !== "") &&
                <div className="flex flex-column">
                    <div className="scan-command">
                        {t("scan_lane_close").toUpperCase().split(" ").map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                    <div className="scan-feedback-area">
                        <p>{t("single_item_orders")}</p>
                    </div>
                </div>
            }
        </div>
    );
};

export default ZoneStorageSorter1UnloadSingles;
